/**
 * The payment methods a tender can take, and how revenue is split across them.
 *
 * One list, so the tender picker, the recorded tender list, the receipt and the
 * admin revenue tiles never disagree about what payment type 4 is called. The
 * ids are seeded identically on every tenant (restartDB.sql:736) and are the
 * same ids tenderMath.js works with on the backend.
 *
 * Receipts are printed in Spanish whatever the screen language is, the way
 * every other line on them already is, so each method carries its own receipt
 * label rather than going through the translation system.
 */

export const CASH_PAYMENT_TYPE_ID = 3;
export const CARD_PAYMENT_TYPE_ID = 2;

export const PAYMENT_METHODS = [
  { id: CASH_PAYMENT_TYPE_ID, key: 'cash', receiptLabel: 'Efectivo' },
  { id: CARD_PAYMENT_TYPE_ID, key: 'creditDebitCard', receiptLabel: 'Tarjeta' },
  { id: 4, key: 'bankTransfer', receiptLabel: 'Transferencia' },
  { id: 5, key: 'crypto', receiptLabel: 'Cripto' },
  { id: 1, key: 'unknownOther', receiptLabel: 'Otro' }
];

const findMethod = (paymentTypeId) =>
  PAYMENT_METHODS.find(method => method.id === Number(paymentTypeId));

// Translation key for a payment type id, for anything drawn on screen.
export const paymentMethodKey = (paymentTypeId) =>
  (findMethod(paymentTypeId) || {}).key || 'unknownOther';

// Spanish label for a payment type id, for anything printed on a receipt.
export const receiptPaymentLabel = (paymentTypeId) =>
  (findMethod(paymentTypeId) || {}).receiptLabel || 'Otro';

// Total of the tips carried by a set of tenders.
export const tenderTipTotal = (payments) =>
  (Array.isArray(payments) ? payments : []).reduce((sum, row) => sum + (Number(row.tip) || 0), 0);

// The three buckets the admin revenue tiles report.
const bucketForPaymentTypeId = (paymentTypeId) => {
  if (Number(paymentTypeId) === CASH_PAYMENT_TYPE_ID) return 'cash';
  if (Number(paymentTypeId) === CARD_PAYMENT_TYPE_ID) return 'card';
  return 'other';
};

const bucketForPaymentTypeName = (name) => {
  if (name === 'Cash') return 'cash';
  if (name === 'Credit/Debit Card') return 'card';
  return 'other';
};

/**
 * Splits closed orders into cash, card and other, by what was actually tendered.
 *
 * orders.payment_type_id names the LARGEST tender, so adding a whole order's
 * revenue to the bucket that column points at reports a 600 cash plus 400 card
 * split as 1000 cash. Where an order carries its tenders, each tender goes in
 * its own bucket for its own amount, and the order appears in every list it
 * paid into.
 *
 * An order closed before the tender ledger existed carries no tenders, so it
 * falls back to its single payment type and reports the number it reports
 * today. An order with neither tenders nor a payment type is in no bucket,
 * which is also what happens today.
 *
 * Tender amounts cover the bill the customer was handed, items plus any service
 * charge, while order_revenue is the item revenue alone. On a tenant with the
 * service charge off the two are the same number.
 *
 * @param {object[]} orders - Closed orders, each optionally carrying `payments`
 * @returns {object} { revenue: {cash, card, other}, orders: {cash, card, other} }
 */
export const bucketRevenueByTender = (orders) => {
  const revenue = { cash: 0, card: 0, other: 0 };
  const lists = { cash: [], card: [], other: [] };

  (Array.isArray(orders) ? orders : []).forEach(order => {
    const tenders = Array.isArray(order.payments) ? order.payments : [];

    if (tenders.length > 0) {
      tenders.forEach(tender => {
        const bucket = bucketForPaymentTypeId(tender.payment_type_id);
        revenue[bucket] += Number(tender.amount) || 0;
        if (!lists[bucket].includes(order)) {
          lists[bucket].push(order);
        }
      });
      return;
    }

    if (order.payment_type_name) {
      const bucket = bucketForPaymentTypeName(order.payment_type_name);
      revenue[bucket] += Number(order.order_revenue) || 0;
      lists[bucket].push(order);
    }
  });

  return { revenue, orders: lists };
};
